import { useEffect, useMemo, useState } from "react";
import { fetchWithAuth } from "../../../utils/fetchWithAuth";
import { API_BASE } from "../../../config";
import { FolderOpen, Check, Document } from "../../icons";
import {
    ResponsiveContainer,
    AreaChart,
    Area,
} from "recharts";

function toNumber(value, fallback = 0) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

function formatInt(value) {
    return Number(value ?? 0).toLocaleString("de-DE");
}

function normalizeStatusSummary(raw) {
    const payload = raw?.data ?? raw ?? {};
    const byStatus = payload?.summaries?.by_status || payload?.by_status || {};

    const openCurrent = toNumber(byStatus?.OPEN?.current, 0);
    const openPrevious = toNumber(byStatus?.OPEN?.previous, 0);
    const closedCurrent = toNumber(byStatus?.CLOSED?.current, 0);
    const closedPrevious = toNumber(byStatus?.CLOSED?.previous, 0);

    return {
        openCurrent,
        openPrevious,
        closedCurrent,
        closedPrevious,
        totalCurrent: openCurrent + closedCurrent,
        totalPrevious: openPrevious + closedPrevious,
    };
}

function normalizeTrendRows(raw) {
    const payload = raw?.data ?? raw ?? {};
    const rows = payload?.data || payload?.rows || payload?.items || [];

    if (!Array.isArray(rows)) return [];

    return rows.map((row) => {
        const statuses = row.statuses || row.by_status || row;

        return {
            date: row.date || row.day || row.period || "",
            open: toNumber(statuses.OPEN ?? statuses.open, 0),
            closed: toNumber(statuses.CLOSED ?? statuses.closed, 0),
            total: toNumber(
                statuses.TOTAL ?? statuses.total ?? row.total_cases,
                toNumber(statuses.OPEN ?? statuses.open, 0) +
                toNumber(statuses.CLOSED ?? statuses.closed, 0)
            ),
        };
    });
}

function getDelta(current, previous, lowerIsBetter = false) {
    if (!previous) {
        return {
            label: "No comparison data",
            className: "stat-delta--neutral",
        };
    }

    const diff = current - previous;
    const pct = (diff / previous) * 100;
    const sign = pct >= 0 ? "+" : "";
    const isPositive = lowerIsBetter ? pct <= 0 : pct >= 0;

    return {
        label: `${sign}${pct.toFixed(1)}% vs last week (${formatInt(previous)})`,
        className: isPositive ? "stat-delta--down" : "stat-delta--up",
    };
}

function MetricCard({
    title,
    value,
    previous,
    dataKey,
    rows,
    Icon,
    lowerIsBetter = false,
}) {
    const delta = getDelta(value, previous, lowerIsBetter);

    return (
        <div className="rd-kpi-card">
            <div className="rd-kpi-top">
                <div className="rd-kpi-icon">
                    <Icon width={26} height={26} />
                </div>

                <div>
                    <div className="rd-kpi-label">{title}</div>
                    <div className="rd-kpi-value">{formatInt(value)}</div>
                    <div className={`rd-kpi-delta ${delta.className}`}>{delta.label}</div>
                </div>
            </div>

            <div className="rd-kpi-sparkline">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={rows}>
                        <defs>
                            <linearGradient id={`spark-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#6F3AFF" stopOpacity={0.5} />
                                <stop offset="95%" stopColor="#6F3AFF" stopOpacity={0.02} />
                            </linearGradient>
                        </defs>

                        <Area
                            type="monotone"
                            dataKey={dataKey}
                            stroke="#6F3AFF"
                            strokeWidth={2.2}
                            fill={`url(#spark-${dataKey})`}
                            dot={false}
                            isAnimationActive={false}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

export default function KeyPerformanceMetrics() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [summary, setSummary] = useState(null);
    const [trendRows, setTrendRows] = useState([]);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            try {
                setLoading(true);
                setError("");

                const [summaryResponse, trendResponse] = await Promise.all([
                    fetchWithAuth(`${API_BASE}/ainstein/in/dashboard?chart_key=cases_by_status`),
                    fetchWithAuth(`${API_BASE}/ainstein/in/dashboard?chart_key=case_trend_by_status`),
                ]);

                if (!summaryResponse.ok) {
                    throw new Error(`Status summary failed (${summaryResponse.status})`);
                }

                if (!trendResponse.ok) {
                    throw new Error(`Status trend failed (${trendResponse.status})`);
                }

                const summaryPayload = await summaryResponse.json();
                const trendPayload = await trendResponse.json();

                if (!cancelled) {
                    setSummary(normalizeStatusSummary(summaryPayload));
                    setTrendRows(normalizeTrendRows(trendPayload));
                    setLoading(false);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err?.message || "Failed to load key performance metrics");
                    setSummary(null);
                    setTrendRows([]);
                    setLoading(false);
                }
            }
        };

        load();

        return () => {
            cancelled = true;
        };
    }, []);

    const fallbackRows = useMemo(() => {
        if (trendRows.length) return trendRows;

        return [
            { open: 0, closed: 0, total: 0 },
            { open: 0, closed: 0, total: 0 },
        ];
    }, [trendRows]);

    return (
        <section className="rd-visual-card rd-kpi-section">
            <div className="rd-visual-title-row">
                <span className="rd-visual-icon-dot rd-visual-icon-dot--trend" />
                <h3 className="rd-visual-card-title">Key Performance Metrics</h3>
            </div>

            {loading ? (
                <div className="remediation-dashboard-loading">
                    <span className="remediation-dashboard-loading-spinner" />
                    <span>Loading key metrics...</span>
                </div>
            ) : error ? (
                <div className="remediation-dashboard-empty-state">{error}</div>
            ) : !summary ? (
                <div className="remediation-dashboard-empty-state">
                    No key metric data available.
                </div>
            ) : (
                <div className="rd-kpi-grid">
                    <MetricCard
                        title="Open Cases"
                        value={summary.openCurrent}
                        previous={summary.openPrevious}
                        dataKey="open"
                        rows={fallbackRows}
                        Icon={FolderOpen}
                        lowerIsBetter
                    />

                    <MetricCard
                        title="Closed Cases"
                        value={summary.closedCurrent}
                        previous={summary.closedPrevious}
                        dataKey="closed"
                        rows={fallbackRows}
                        Icon={Check}
                    />

                    <MetricCard
                        title="Total Cases"
                        value={summary.totalCurrent}
                        previous={summary.totalPrevious}
                        dataKey="total"
                        rows={fallbackRows}
                        Icon={Document}
                        lowerIsBetter
                    />
                </div>
            )}
        </section>
    );
}